import { FormikProps } from "formik";
import React from "react";
import { TripFormValues } from "../types/tripTypes";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Fade,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RichTextEditor from "components/shared/TextEditor/RTE";

interface InclusionProps {
  formik: FormikProps<TripFormValues>;
  editable?: boolean;
}

const defaultInclusions = [0, 1, 2];

const Inclusion: React.FC<InclusionProps> = ({ formik, editable = true }) => {
  const { values, setFieldValue } = formik;
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);

  const hasOthersInclusion = values?.inclusion.some(
    (item) => !defaultInclusions.includes(item.id)
  );

  const handleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updated = [...values?.inclusion];
    updated[index].description = value;
    setFieldValue("inclusion", updated);
  };

  const addOtherInclusion = () => {
    if (hasOthersInclusion) return;

    const newInclusion = {
      id: Date.now(), // ensure unique id
      label: "Others",
      description: "",
    };
    setFieldValue("inclusion", [...values?.inclusion, newInclusion]);
    setExpandedIndex(values?.inclusion?.length);
  };

  const removeInclusion = (index: number) => {
    const updated = [...values?.inclusion];
    updated.splice(index, 1);
    setFieldValue("inclusion", updated);

    // Adjust expanded index if necessary
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  return (
    <Card
      sx={{
        marginTop: 3,
        padding: 2,
        width: "100%",
        marginX: "auto",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderRadius: 2,
        border: "1px solid #E8EAED",
      }}
    >
      <CardContent sx={{ padding: 3 }}>
        <Box sx={{ marginBottom: 3 }}>
          <Typography
            fontWeight={600}
            variant="h6"
            color="#181D27"
            sx={{ marginBottom: 0.5, fontSize: "20px" }}
          >
            Inclusions
          </Typography>
          <Typography
            variant="body2"
            color="#535862"
            sx={{ fontSize: "14px", lineHeight: 1.5 }}
          >
            Describe what is included in the trip to help travelers understand
            the value.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {values?.inclusion?.map((item, index) => {
            const isExpanded = expandedIndex === index;
            const isCustom = !defaultInclusions.includes(item?.id);

            return (
              <Accordion
                key={item?.id}
                expanded={isExpanded}
                onChange={() => handleExpand(index)}
                sx={{
                  "&:before": { display: "none" },
                  border: "1px solid #E0E3E7",
                  borderRadius: "12px !important",
                  boxShadow: isExpanded
                    ? "0 4px 12px rgba(0,0,0,0.1)"
                    : "0 1px 3px rgba(0,0,0,0.05)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderColor: "#C1C7CD",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: "#535862",
                        transition: "transform 0.2s ease-in-out",
                        flexShrink: 0,
                      }}
                    />
                  }
                  sx={{
                    margin: 0,
                    padding: "8px 20px",
                    minHeight: "60px",
                    "&.Mui-expanded": {
                      minHeight: "60px",
                    },
                    "& .MuiAccordionSummary-content": {
                      minWidth: 0,
                      overflow: "hidden",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      minWidth: 0,
                      gap: 2,
                      flex: 1,
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="#181D27"
                      fontWeight={600}
                      sx={{
                        flexShrink: 0,
                        fontSize: "16px",
                      }}
                    >
                      {item.label}
                    </Typography>

                    {!isExpanded && (
                      <Typography
                        variant="body2"
                        color="#9CA3AF"
                        sx={{
                          fontSize: "14px",
                          fontStyle: "italic",
                        }}
                      >
                        Add inclusion details here
                      </Typography>
                    )}
                    {/* Delete button for custom inclusions */}
                    {editable && isCustom && (
                      <Tooltip title="Remove this inclusion" arrow>
                        <IconButton
                          aria-label="delete"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeInclusion(index);
                          }}
                          sx={{
                            marginLeft: "auto",
                            color: "#6B7280",
                            flexShrink: 0,
                            "&:hover": {
                              color: "#DC2626",
                              backgroundColor: "rgba(220, 38, 38, 0.1)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ padding: "0 20px 20px 20px" }}>
                  <Box sx={{ borderTop: "1px solid #F3F4F6", paddingTop: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={500}
                      sx={{
                        marginBottom: 1.5,
                        color: "#374151",
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      Description *
                    </Typography>
                    <RichTextEditor
                      value={item?.description || ""}
                      onChange={(value) =>
                        handleDescriptionChange(index, value)
                      }
                      placeholder={`Describe what's included in ${item?.label?.toLowerCase()}...`}
                      disabled={!editable}
                      maxLength={400}
                      minHeight={140}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        {/* Add Others Button */}
        {editable && !hasOthersInclusion && (
          <Fade in={true} timeout={300}>
            <Box
              onClick={addOtherInclusion}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                color: "#374151",
                fontWeight: 500,
                marginTop: 3,
                border: "2px dashed #D1D5DB",
                borderRadius: "12px",
                padding: "16px 24px",
                minHeight: "56px",
                fontFamily: "Inter, sans-serif",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                backgroundColor: "#FAFBFC",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  borderColor: "#9CA3AF",
                  color: "#1F2937",
                },
                "&:active": {
                  transform: "translateY(1px)",
                },
              }}
            >
              <AddIcon sx={{ fontSize: "20px" }} />
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                Add Other Inclusion
              </Typography>
            </Box>
          </Fade>
        )}
      </CardContent>
    </Card>
  );
};

export default Inclusion;
